// ─────────────────────────────────────────────────────────────────
// Glass-i — Stage 6B.8A-3G: UOM conversion utility.
//
// One source of truth for "price per X UOM" ↔ "price per Y UOM"
// conversions used by the simple-create popups today and by the
// billing module tomorrow. Pure functions — no React, no axios — so
// node:test can exercise this without a DOM.
//
// Conversion philosophy:
//   • Area     — SQM ⇄ SQF (1 SQM = 10.764 SQF; international + Indian
//                GST standard).
//   • Length   — MTR ⇄ RFT / FT (1 MTR = 3.28084 FT; RFT is "running
//                feet", same as FT for linear pricing).
//   • Weight   — KGS / KG ⇄ G (1 KG = 1000 G). Within-family pricing
//                is 1:1 for KGS as a primary UOM.
//   • Discrete — PCS / SET / NOS / QTY are by definition 1 unit each;
//                no conversion is meaningful. Returns null so the UI
//                can hide the "≈ … per ALT" hint instead of showing
//                a redundant 1:1 line.
//
// All conversions are direct multiplications. The factor is computed
// from the lookup once and inverted as needed so callers don't have to
// guess the order. Internally codes are upper-cased and trimmed so the
// utility is forgiving of how the UOM was sourced (master record vs
// hand-typed legacy data).
// ─────────────────────────────────────────────────────────────────

// 1 unit of LEFT = (factor) units of RIGHT.
const FACTOR_TABLE = Object.freeze({
  // Area
  'SQM->SQF': 10.764,
  // Length / running feet
  'MTR->RFT': 3.28084,
  'MTR->FT':  3.28084,
  'RFT->FT':  1,
  // Weight
  'KG->G':    1000,
  'KGS->G':   1000,
  'KG->KGS':  1,
  'KGS->KG':  1
});

// Default "alt" UOM shown beside a primary code in the picker hint.
// Used by the popup to render "Selling Price ≈ X per <ALT>" without
// the caller specifying both sides. Discrete UOMs map to null so the
// hint is suppressed for them.
const ALT_UOM = Object.freeze({
  SQM: 'SQF',
  SQF: 'SQM',
  MTR: 'RFT',
  RFT: 'MTR',
  FT:  'MTR',
  KG:  'KGS',
  KGS: 'KG',
  PCS: null,
  PIECE: null,
  SET:  null,
  SETS: null,
  NOS:  null,
  QTY:  null,
  EA:   null,
  EACH: null
});

function norm(code) {
  if (code == null) return null;
  const s = String(code).trim().toUpperCase();
  return s ? s : null;
}

// Returns the factor F such that: 1 unit of `from` = F units of `to`.
// Returns null when no conversion is known (callers MUST treat this as
// "no equivalence available" — never silently fall back to 1).
export function getConversionFactor(fromCode, toCode) {
  const a = norm(fromCode);
  const b = norm(toCode);
  if (!a || !b) return null;
  if (a === b) return 1;
  const direct = FACTOR_TABLE[`${a}->${b}`];
  if (direct != null) return direct;
  const inverse = FACTOR_TABLE[`${b}->${a}`];
  if (inverse != null && inverse !== 0) return 1 / inverse;
  return null;
}

// Convert a per-unit price across UOMs.
//   price (number) — amount per `fromCode`
//   returns        — equivalent amount per `toCode`, or null if no
//                    known conversion exists between the two codes.
//
// Math: if 1 fromUom = F toUom, then 1 toUom = (1 / F) fromUom; and a
// price expressed per fromUom translates to price per toUom by
// dividing by F.
//
//   Example: ₹100 / SQM and we ask for per SQF.
//     factor (SQM->SQF) = 10.764
//     → ₹100 / 10.764 ≈ ₹9.29 per SQF
//
// Discrete UOMs (PCS, SET, QTY…) — function returns null because two
// discrete units are not commensurable. The caller suppresses the hint.
export function convertPricePerUnit(price, fromCode, toCode) {
  if (price == null || price === '') return null;
  const n = Number(price);
  if (!Number.isFinite(n)) return null;
  const f = getConversionFactor(fromCode, toCode);
  if (f == null) return null;
  return n / f;
}

// The natural "show-this-alongside" UOM for a primary code. SQM → SQF,
// MTR → RFT, etc. Discrete codes return null so the UI knows to hide
// the equivalence hint instead of rendering a redundant 1:1.
export function altUomFor(code) {
  const c = norm(code);
  if (!c) return null;
  if (Object.prototype.hasOwnProperty.call(ALT_UOM, c)) return ALT_UOM[c];
  // Unknown UOM codes (custom-typed by master admins) — no hint until
  // an explicit alt is registered.
  return null;
}

// Discrete UOMs are commensurable 1:1 with themselves (pricing stays
// the same). Exposed so callers can short-circuit hint rendering when
// the user picks PCS / SET / QTY / NOS.
const DISCRETE_UOMS = Object.freeze(new Set([
  'PCS', 'PIECE', 'SET', 'SETS', 'NOS', 'NO', 'QTY', 'EA', 'EACH', 'UNIT', 'UNITS'
]));
export function isDiscreteUom(code) {
  const c = norm(code);
  return c ? DISCRETE_UOMS.has(c) : false;
}

// Format a numeric price for the hint line — Indian-style 2-decimal,
// no currency symbol (caller supplies if it wants one). Returns '' for
// null / NaN so the hint can render safely without a guard at the call
// site.
export function formatPrice(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toFixed(2);
}

export default {
  getConversionFactor,
  convertPricePerUnit,
  altUomFor,
  isDiscreteUom,
  formatPrice
};
